import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Plus, Trash2, Edit, Wallet } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatDate = (d) => {
  if (!d) return '-';
  try {
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return d;
  }
};

const BalanceLedger = ({ type, title, description }) => {
  const [accounts, setAccounts] = useState([]);
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);

  const [addAcctOpen, setAddAcctOpen] = useState(false);
  const [newAcctName, setNewAcctName] = useState('');

  const [balDialogOpen, setBalDialogOpen] = useState(false);
  const [editingBal, setEditingBal] = useState(null);
  const [balForm, setBalForm] = useState({
    account_id: '',
    amount: '',
    balance_date: new Date().toISOString().split('T')[0],
    note: '',
  });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [accRes, balRes] = await Promise.all([
        axios.get(`${API}/finance/accounts?type=${type}`, { withCredentials: true }),
        axios.get(`${API}/finance/balances?type=${type}`, { withCredentials: true }),
      ]);
      setAccounts(accRes.data || []);
      setBalances(balRes.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [type]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const latestByAccount = useMemo(() => {
    const map = {};
    balances.forEach((b) => {
      if (!map[b.account_id] || new Date(b.balance_date) > new Date(map[b.account_id].balance_date)) {
        map[b.account_id] = b;
      }
    });
    return map;
  }, [balances]);

  const totalCurrent = useMemo(
    () => Object.values(latestByAccount).reduce((s, b) => s + (b.amount || 0), 0),
    [latestByAccount]
  );

  const addAccount = async (e) => {
    e.preventDefault();
    if (!newAcctName.trim()) return;
    try {
      await axios.post(`${API}/finance/accounts`, { name: newAcctName.trim(), type }, { withCredentials: true });
      toast.success('Account created');
      setNewAcctName('');
      setAddAcctOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const deleteAccount = async (id) => {
    if (!window.confirm('Delete this account? Only empty accounts can be deleted.')) return;
    try {
      await axios.delete(`${API}/finance/accounts/${id}`, { withCredentials: true });
      toast.success('Account deleted');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const openBalDialog = (bal = null) => {
    if (bal) {
      setEditingBal(bal);
      setBalForm({
        account_id: bal.account_id,
        amount: String(bal.amount),
        balance_date: bal.balance_date ? new Date(bal.balance_date).toISOString().split('T')[0] : '',
        note: bal.note || '',
      });
    } else {
      setEditingBal(null);
      setBalForm({
        account_id: accounts[0]?.id || '',
        amount: '',
        balance_date: new Date().toISOString().split('T')[0],
        note: '',
      });
    }
    setBalDialogOpen(true);
  };

  const submitBal = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        amount: parseFloat(balForm.amount || 0),
        balance_date: new Date(balForm.balance_date).toISOString(),
        note: balForm.note || undefined,
      };
      if (editingBal) {
        await axios.put(`${API}/finance/balances/${editingBal.id}`, payload, { withCredentials: true });
        toast.success('Balance updated');
      } else {
        await axios.post(`${API}/finance/balances`, { ...payload, account_id: balForm.account_id }, { withCredentials: true });
        toast.success('Balance recorded');
      }
      setBalDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const deleteBal = async (id) => {
    if (!window.confirm('Delete this balance entry?')) return;
    try {
      await axios.delete(`${API}/finance/balances/${id}`, { withCredentials: true });
      toast.success('Entry deleted');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  return (
    <Card data-testid={`ledger-${type.toLowerCase()}`}>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="font-heading flex items-center gap-2">
            <Wallet className="h-5 w-5 text-primary" /> {title}
          </CardTitle>
          <CardDescription>{description}</CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right">
            <div className="text-xs text-muted-foreground">Current total</div>
            <div className="text-lg font-bold text-primary font-mono">{formatINR(totalCurrent)}</div>
          </div>
          <Dialog open={addAcctOpen} onOpenChange={setAddAcctOpen}>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAddAcctOpen(true)}
              data-testid={`add-${type.toLowerCase()}-account`}
            >
              <Plus className="h-4 w-4 mr-1" /> Account
            </Button>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>New {type === 'BANK' ? 'Bank' : 'FD'} Account</DialogTitle>
              </DialogHeader>
              <form onSubmit={addAccount} className="space-y-3">
                <div className="space-y-1">
                  <Label>Name</Label>
                  <Input
                    value={newAcctName}
                    onChange={(e) => setNewAcctName(e.target.value)}
                    placeholder={type === 'BANK' ? 'e.g. ICICI Bank' : 'e.g. FD-Main'}
                    required
                    data-testid={`${type.toLowerCase()}-account-name-input`}
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setAddAcctOpen(false)}>Cancel</Button>
                  <Button type="submit" data-testid={`submit-${type.toLowerCase()}-account`}>Create</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          <Button
            size="sm"
            onClick={() => openBalDialog()}
            disabled={accounts.length === 0}
            data-testid={`add-${type.toLowerCase()}-balance`}
          >
            <Plus className="h-4 w-4 mr-1" /> Balance
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Accounts summary */}
        {accounts.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {accounts.map((acc) => {
              const latest = latestByAccount[acc.id];
              return (
                <div
                  key={acc.id}
                  className="p-3 rounded border bg-muted/30 flex items-center justify-between"
                >
                  <div className="min-w-0">
                    <div className="font-medium truncate">{acc.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {latest ? `as of ${formatDate(latest.balance_date)}` : 'no entries yet'}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-semibold text-primary whitespace-nowrap">
                      {formatINR(latest?.amount || 0)}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteAccount(acc.id)}
                      title="Delete account"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Ledger table */}
        {loading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Loading...</div>
        ) : balances.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            No balance entries yet. {accounts.length === 0 ? 'Create an account first.' : 'Click "Balance" to add one.'}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Account</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Note</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {balances.slice(0, 50).map((b) => (
                <TableRow key={b.id} data-testid={`balance-row-${b.id}`}>
                  <TableCell className="font-medium">{formatDate(b.balance_date)}</TableCell>
                  <TableCell>{b.account_name}</TableCell>
                  <TableCell className="font-mono">{formatINR(b.amount)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-xs truncate">
                    {b.note || '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => openBalDialog(b)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteBal(b.id)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* Balance dialog */}
        <Dialog open={balDialogOpen} onOpenChange={setBalDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingBal ? 'Edit' : 'New'} Balance Entry</DialogTitle>
              <DialogDescription>
                Enter the balance amount on a particular date.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submitBal} className="space-y-3">
              <div className="space-y-1">
                <Label>Account *</Label>
                <Select
                  value={balForm.account_id}
                  onValueChange={(v) => setBalForm({ ...balForm, account_id: v })}
                  disabled={!!editingBal}
                >
                  <SelectTrigger data-testid={`${type.toLowerCase()}-balance-account-select`}>
                    <SelectValue placeholder="Select account" />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    required
                    value={balForm.balance_date}
                    onChange={(e) => setBalForm({ ...balForm, balance_date: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Amount (₹) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    required
                    value={balForm.amount}
                    onChange={(e) => setBalForm({ ...balForm, amount: e.target.value })}
                    data-testid={`${type.toLowerCase()}-balance-amount-input`}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label>Note</Label>
                <Input
                  value={balForm.note}
                  onChange={(e) => setBalForm({ ...balForm, note: e.target.value })}
                  placeholder="Optional"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setBalDialogOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid={`submit-${type.toLowerCase()}-balance`}>
                  {editingBal ? 'Update' : 'Save'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default BalanceLedger;
